import React from 'react';
import PropTypes from 'prop-types';
import { formatCurrency, formatNumber, formatWithLocale } from '@adjust/dashboard-core/lib/format/number';
import {
  TileWithLoading,
  tileWithLoadingPropTypes,
} from '@react/Pages/AccountSettingsPage/YourDataSection/TileWithLoading';

import { getExtraCharge } from './PricingPlanTile.utils';

const PricingPlanTileBase = ({ currentAccount, currentCharge }) => {
  const account = currentAccount ?? {};
  const charge = currentCharge ?? {};
  const extraCharge = account.payment_plan && getExtraCharge(account.payment_plan, charge.totalAttributions);
  const formatAttributionPrice = formatWithLocale({
    style: 'currency',
    currency: account.currency_iso_code,
    maximumFractionDigits: 8,
  });

  return (
    <>
      <div className="your-data-card__title">{t('label.pricing-plan')}</div>
      <div className="your-data-card__body">
        {account.payment_plan && (
          <div className="pricing-plan-container">
            <div className="attributions-usage">
              <div className="attributions-usage-left">{t('label.included-attributions')}</div>
              <div className="attributions-usage-right">
                {account.payment_plan.included_attributions === -1 ? (
                  <span>{t('label.unlimited')}</span>
                ) : (
                  <span>{formatNumber(account.payment_plan.included_attributions)}</span>
                )}
              </div>
              <div className="attributions-usage-bar">
                {Object.keys(charge).length > 0 && account.payment_plan.included_attributions !== -1 && (
                  <div className="attributions-usage-progress" style={{ width: extraCharge.progress }}>
                    {formatNumber(charge.totalAttributions)}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-box-row">
              <div className="flex-one">
                <p className="payment-plan-name">{account.payment_plan.name}</p>
                <p className="payment-plan-price">
                  {formatCurrency({ currency: account.currency_iso_code })(account.payment_plan.base_price)}
                </p>
                <p className="payment-plan-description">
                  {account.payment_plan.yearly ? t('label.yearly-fee') : t('label.monthly-fee')}
                </p>
              </div>
              {account.payment_plan.included_attributions !== -1 && (
                <div className="flex-one payment-plan-center">
                  <p className="payment-plan-name">
                    {extraCharge.additionalAttributions}{' '}
                    <span className="payment-plan-light">
                      &times; {formatAttributionPrice(account.payment_plan.attribution_price)}
                    </span>
                  </p>
                  <p className="payment-plan-price">
                    {formatCurrency({ currency: account.currency_iso_code })(extraCharge.total)}
                  </p>
                  <p className="payment-plan-description">{t('label.additional-attributions')}</p>
                </div>
              )}

              <div className="flex-one payment-plan-right">
                <p className="payment-plan-name">&nbsp;</p>
                <p className="payment-plan-price">
                  {charge.totalPriceNet ||
                    formatCurrency({ currency: account.currency_iso_code })(account.payment_plan.base_price)}
                </p>
                <p className="payment-plan-description">{t('label.total')}</p>
              </div>
            </div>
          </div>
        )}

        {!account.payment_plan && (
          <div className="no-data">
            <p>{t('pricing-plan.missing-pricing-plan.message')}</p>
          </div>
        )}
      </div>
    </>
  );
};

PricingPlanTileBase.propTypes = {
  currentAccount: PropTypes.shape({
    payment_plan: PropTypes.shape({
      base_price: PropTypes.number,
      attribution_price: PropTypes.number,
      included_attributions: PropTypes.number,
      name: PropTypes.string,
      yearly: PropTypes.bool,
    }),
    currency_iso_code: PropTypes.string,
  }),
  currentCharge: PropTypes.shape({
    totalAttributions: PropTypes.number,
    totalPriceNet: PropTypes.string,
  }),
};

const PricingPlanTile = TileWithLoading(PricingPlanTileBase);

PricingPlanTile.propTypes = {
  ...PricingPlanTileBase.propTypes,
  ...tileWithLoadingPropTypes,
};

export default PricingPlanTile;
